"use client"

import React from "react"

import { useMemo, useState, useEffect, useCallback, useContext } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  alpha,
  Alert,
  Snackbar,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility"
import ReplayIcon from "@mui/icons-material/Replay"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import FilterListIcon from "@mui/icons-material/FilterList"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import PersonIcon from "@mui/icons-material/Person"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import HomeIcon from "@mui/icons-material/Home"
import CheckIcon from "@mui/icons-material/Check"
import ErrorIcon from "@mui/icons-material/Error"

import { ThemeProvider, createTheme } from "@mui/material/styles"
import { GridFooterContainer, GridFooter } from "@mui/x-data-grid"
import {
  useDataGridRowContextMenu,
  DataGridRowContextMenu,
  DataGridActionCell,
} from "../misc/DataGridContextMenu.js"

import { ApiContextDominios } from "../../context/apiContextDominios.js"

import dayjs from "dayjs"

/** Valor usado para filtrar por ID (evita strings vacíos). */
const normalizeIdTransaccionSearch = (raw) => {
  if (raw == null || raw === "") return null
  const s = String(raw).trim()
  return s === "" ? null : s
}

const isDateInRange = (orderDate, startDate, endDate) => {
  const orderDayjs = dayjs(orderDate, "DD/MM/YYYY HH:mm")
  const start = startDate.startOf("day")
  const end = endDate.endOf("day")
  return orderDayjs.isAfter(start) && orderDayjs.isBefore(end)
}

const mapBackendPedidoRow = (row) => ({
  id: row.id,
  createdAt: row.fechaHoraCreacion
    ? dayjs(row.fechaHoraCreacion).format("DD/MM/YYYY HH:mm")
    : "",
  montoTotal: row.montoTotal,
  descripcionEntidad: row.descripcionEntidad || "",
  apellido: row.apellido || "",
  dniCuitCuil: row.dniCuitCuil || "",
  direccion: row.direccion || "",
  estado: row.estado || "pendiente",
  usuario: row.usuario || "",
  ubicacion: row.ubicacion || "",
  idUbicacion: row.idUbicacion,
  medioPago: row.medioPago || "Sin definir",
  fechaHoraCreacion: row.fechaHoraCreacion,
  fechaActualizacion: row.fechaActualizacion || row.fechaHoraCreacion,
  items: [],
})


const ubicaciones = [
  { id: 1, descripcion: "Amazon" },
  { id: 2, descripcion: "Mercado Libre" },
]

const entidadesClientesTresLetras = [
  { id: 1, descripcion: "Juan Pérez", apellido: "González", dniCuitCuil: "12345678" },
  { id: 2, descripcion: "Ana Rodríguez", apellido: "Silva", dniCuitCuil: "87654321" },
  { id: 3, descripcion: "Roberto Silva", apellido: "Martínez", dniCuitCuil: "11223344" },
  { id: 4, descripcion: "Sofía González", apellido: "Torres", dniCuitCuil: "55667788" },
  { id: 5, descripcion: "Miguel Torres", apellido: "Vega", dniCuitCuil: "99887766" },
  { id: 6, descripcion: "Carmen Vega", apellido: "Castro", dniCuitCuil: "44556677" },
  { id: 7, descripcion: "Luis Morales", apellido: "Díaz", dniCuitCuil: "33445566" },
  { id: 8, descripcion: "Elena Vargas", apellido: "Ruiz", dniCuitCuil: "77889900" },
  { id: 9, descripcion: "Pedro Ramírez", apellido: "Jiménez", dniCuitCuil: "11998877" },
  { id: 10, descripcion: "Claudia Herrera", apellido: "Mendoza", dniCuitCuil: "22334455" },
  { id: 11, descripcion: "Fernando Castro", apellido: "Moreno", dniCuitCuil: "66778899" },
  { id: 12, descripcion: "Gabriela Soto", apellido: "Álvarez", dniCuitCuil: "44556677" },
  { id: 13, descripcion: "Ricardo Blanco", apellido: "Navarro", dniCuitCuil: "88990011" },
  { id: 14, descripcion: "Mónica Delgado", apellido: "Romero", dniCuitCuil: "55443322" },
  { id: 15, descripcion: "Alejandro Paz", apellido: "Guerrero", dniCuitCuil: "77665544" },
  { id: 16, descripcion: "Natalia Campos", apellido: "Herrera", dniCuitCuil: "99887766" },
  { id: 17, descripcion: "Andrés Molina", apellido: "Vargas", dniCuitCuil: "33221100" },
  { id: 18, descripcion: "Verónica Luna", apellido: "Ortega", dniCuitCuil: "11223344" },
  { id: 19, descripcion: "Gustavo Ramos", apellido: "Silva", dniCuitCuil: "66554433" },
  { id: 20, descripcion: "Patricia Flores", apellido: "Medina", dniCuitCuil: "88776655" },
]

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
})

const estadoConfig = {
  pendiente: {
    color: "#FFA726",
    bgColor: alpha("#FFA726", 0.1),
    icon: HourglassEmptyIcon,
    label: "Pendiente",
    nextStates: ["recibido"], // Solo puede pasar a recibido
  },
  recibido: {
    color: "#42A5F5",
    bgColor: alpha("#42A5F5", 0.1),
    icon: CheckCircleIcon,
    label: "Recibido",
    nextStates: ["despachado"], // Solo puede pasar a despachado
  },
  despachado: {
    color: "#FF7043",
    bgColor: alpha("#FF7043", 0.1),
    icon: LocalShippingIcon,
    label: "Despachado",
    nextStates: ["entregado"], // Solo puede pasar a entregado
  },
  entregado: {
    color: "#66BB6A",
    bgColor: alpha("#66BB6A", 0.1),
    icon: HomeIcon,
    label: "Entregado",
    nextStates: [], // Estado final
  },
}

export default function SimulacionEstados() {
  // Estados principales
  const [openModal, setOpenModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const {
    getPedidos,
    getPedidoDetail,
    cambiarEstadoPedido,
  } = useContext(ApiContextDominios)
  const [rows, setRows] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const today = dayjs()
  const [startDate, setStartDate] = useState(today.subtract(30, "day"))
  const [endDate, setEndDate] = useState(today)
  const [minVentas, setMinVentas] = useState(0)
  const [maxVentas, setMaxVentas] = useState(0)
  const [loading, setLoading] = useState(false)
  const [dniCliente, setDniCliente] = useState(null)
  const [mostrarDeletes, setMostrarDeletes] = useState(false)
  const [idTransaccionSearch, setIdTransaccionSearch] = useState("")
  const [idTransaccionSearchDebounced, setIdTransaccionSearchDebounced] = useState(null)
  const [estadoFilter, setEstadoFilter] = useState("")

  // Medio de pago hardcodeado para MVP: Efectivo / Efectivo (id=1).
  // Cuando la transición es pendiente→recibido el frontend emula la selección
  // del operador y el backend crea el Pago igual que antes.
  const MEDIO_PAGO_DEFAULT = { id: 1, descripcion: "Efectivo", tipoDescripcion: "Efectivo" }
  const [savingEstado, setSavingEstado] = useState(false)

  // Estados para notificaciones
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  const increaseDate = () => {
    setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  }

  const decreaseDate = () => {
    setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setIdTransaccionSearchDebounced(normalizeIdTransaccionSearch(idTransaccionSearch))
    }, 300)
    return () => clearTimeout(timer)
  }, [idTransaccionSearch])

  useEffect(() => {
    let cancelled = false
    const fetchPedidos = async () => {
      try {
        setLoading(true)
        const resp = await getPedidos({ page: 1, limit: 500 })
        if (cancelled) return
        const mapped = (resp?.data || []).map(mapBackendPedidoRow)
        setRows(mapped)
      } catch (err) {
        console.error("Error cargando pedidos:", err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchPedidos()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showNotification = (message, severity = "success") => {
    setNotification({
      open: true,
      message,
      severity,
    })
  }

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false })
  }

  const hayFiltrosParaLimpiar = useMemo(() => {
    const now = dayjs()
    const defaultStart = now.subtract(30, "day")
    const defaultEnd = now
    const fechasEnDefault =
      startDate &&
      endDate &&
      dayjs(startDate).isSame(defaultStart, "day") &&
      dayjs(endDate).isSame(defaultEnd, "day")
    const idFiltrado = Boolean(normalizeIdTransaccionSearch(idTransaccionSearch))

    return (
      !fechasEnDefault ||
      Boolean(dniCliente) ||
      Boolean(selectedUbicacion) ||
      minVentas > 0 ||
      maxVentas > 0 ||
      mostrarDeletes ||
      idFiltrado ||
      Boolean(estadoFilter)
    )
  }, [
    startDate,
    endDate,
    dniCliente,
    selectedUbicacion,
    minVentas,
    maxVentas,
    mostrarDeletes,
    idTransaccionSearch,
    estadoFilter,
  ])

  const sortedData = useMemo(() => {
    let filtered = [...rows]

    if (startDate && endDate) {
      filtered = filtered.filter((order) => isDateInRange(order.createdAt, startDate, endDate))
    }

    if (dniCliente) {
      filtered = filtered.filter(
        (order) =>
          order.descripcionEntidad
            .toLowerCase()
            .includes((dniCliente.descripcion || "").toLowerCase()) ||
          order.dniCuitCuil.includes(dniCliente.dniCuitCuil || ""),
      )
    }

    if (selectedUbicacion) {
      filtered = filtered.filter((order) => order.ubicacion === selectedUbicacion.descripcion)
    }

    if (minVentas > 0) {
      filtered = filtered.filter((order) => order.montoTotal >= minVentas)
    }
    if (maxVentas > 0) {
      filtered = filtered.filter((order) => order.montoTotal <= maxVentas)
    }

    if (idTransaccionSearchDebounced) {
      filtered = filtered.filter((order) =>
        order.id.toString().includes(idTransaccionSearchDebounced),
      )
    }

    if (estadoFilter) {
      filtered = filtered.filter((order) => order.estado === estadoFilter)
    }

    return filtered
  }, [
    rows,
    startDate,
    endDate,
    dniCliente,
    selectedUbicacion,
    minVentas,
    maxVentas,
    idTransaccionSearchDebounced,
    estadoFilter,
  ])

  const refreshPedidos = async () => {
    try {
      setLoading(true)
      const resp = await getPedidos({ page: 1, limit: 500 })
      const mapped = (resp?.data || []).map(mapBackendPedidoRow)
      setRows(mapped)
    } catch (err) {
      console.error("Error cargando pedidos:", err)
    } finally {
      setLoading(false)
    }
  }

  const resetFilters = () => {
    setDniCliente(null)
    setSelectedUbicacion(null)
    setMinVentas(0)
    setMaxVentas(0)
    setMostrarDeletes(false)
    setIdTransaccionSearch("")
    setIdTransaccionSearchDebounced(null)
    setEstadoFilter("")
    const d = dayjs()
    setStartDate(d.subtract(30, "day"))
    setEndDate(d)
  }

  const handleMinChange = (e) => {
    const newMin = Number(e.target.value)
    if (newMin <= maxVentas || maxVentas === 0) {
      setMinVentas(newMin)
    }
  }

  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value)
    if (newMax >= minVentas || newMax === 0) {
      setMaxVentas(newMax)
    }
  }

  const handleOpenModal = async (orderRow) => {
    setSelectedOrder({ ...orderRow, items: [] })
    setOpenModal(true)
    try {
      const detail = await getPedidoDetail(orderRow.id)
      const itemsMapeados = (detail?.items || []).map((it) => ({
        idItem: it.idItem,
        codigo: it.item?.codigo || "",
        descripcion: it.item?.descripcion || "",
        cantidad: it.cantidad,
        precio: it.precio,
        montoTotal: it.montoTotal,
      }))
      setSelectedOrder((prev) => (prev && prev.id === orderRow.id ? { ...prev, items: itemsMapeados } : prev))
    } catch (err) {
      console.error("Error cargando detalle del pedido:", err)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedOrder(null)
  }

  const canChangeToState = (currentState, newState) => {
    const config = estadoConfig[currentState]
    return config && config.nextStates.includes(newState)
  }

  const handleStatusChange = (orderId, newStatus) => {
    const order = rows.find((o) => o.id === orderId)
    if (!order) return

    // Validar si el cambio de estado es permitido
    if (!canChangeToState(order.estado, newStatus)) {
      showNotification(
        `No se puede cambiar de "${estadoConfig[order.estado].label}" a "${estadoConfig[newStatus].label}". Debe seguir el flujo: Pendiente → Recibido → Despachado → Entregado`,
        "error",
      )
      return
    }

    // MVP: pendiente→recibido no pide medio de pago, se hardcodea Efectivo (id=1).
    // Cuando los pedidos vengan de sistemas externos el medio real lo mandará
    // ese sistema; mientras tanto se simula la selección desde acá.
    const medioDePagoInfo =
      order.estado === "pendiente" && newStatus === "recibido" ? MEDIO_PAGO_DEFAULT : null

    updateOrderStatus(orderId, newStatus, medioDePagoInfo)
  }

  const updateOrderStatus = async (orderId, newStatus, medioDePagoInfo = null) => {
    setSavingEstado(true)
    try {
      const result = await cambiarEstadoPedido(orderId, newStatus, medioDePagoInfo?.id || null)
      if (!result.ok) {
        showNotification(result.message || "Error al cambiar estado", "error")
        return false
      }

      const now = new Date().toISOString()
      const medioPagoLabel = medioDePagoInfo
        ? medioDePagoInfo.tipoDescripcion
          ? `${medioDePagoInfo.tipoDescripcion} - ${medioDePagoInfo.descripcion}`
          : medioDePagoInfo.descripcion
        : null

      const patchOrder = (order) => {
        if (order.id !== orderId) return order
        const updated = { ...order, estado: newStatus, fechaActualizacion: now }
        if (medioPagoLabel) {
          updated.medioPago = medioPagoLabel
          updated.idMedioDePago = medioDePagoInfo.id
        }
        return updated
      }

      setRows((prev) => prev.map(patchOrder))
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder((prev) => (prev ? patchOrder(prev) : prev))
      }

      const orderActualizada = rows.find((o) => o.id === orderId)
      const paymentInfo = medioPagoLabel ? ` con medio de pago: ${medioPagoLabel}` : ""
      const ventaGenerada = result.data?.ventaGenerada
      const ventaInfo = ventaGenerada ? ` · Venta #${ventaGenerada.idVenta} generada automáticamente` : ""
      showNotification(
        `✅ Pedido #${orderId} de ${orderActualizada?.descripcionEntidad || ""} ${orderActualizada?.apellido || ""} cambió a estado "${estadoConfig[newStatus].label}"${paymentInfo}${ventaInfo}`,
        "success",
      )
      return true
    } finally {
      setSavingEstado(false)
    }
  }

  const getVentasEstadoRowActions = useCallback(
    (row) => [
      {
        key: "ver",
        label: "Ver detalles",
        icon: <VisibilityIcon fontSize="small" />,
        color: "#5b5b5f",
        onSelect: () => handleOpenModal(row),
      },
      {
        key: "pdf",
        label: "Descargar PDF",
        icon: <PictureAsPdfIcon fontSize="small" />,
        color: "rgb(244, 177, 14)",
        onSelect: () => console.log("Descargar PDF", row.id), // eslint-disable-line no-console
      },
      {
        key: "remito",
        label: "Descargar remito",
        icon: <ReceiptLongIcon fontSize="small" />,
        color: "#4CAF50",
        onSelect: () => console.log("Descargar remito", row.id), // eslint-disable-line no-console
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const {
    contextMenu: estadosContextMenu,
    handleContextMenu: handleEstadosContextMenu,
    handleClose: handleEstadosContextMenuClose,
  } = useDataGridRowContextMenu({ getRowActions: getVentasEstadoRowActions, rows: sortedData })

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        flex: 0.3,
        minWidth: 70,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Fecha",
        flex: 1,
        align: "left",
        headerAlign: "left",
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start", gap: 1, alignItems: "center" }}>
            <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "fechaActualizacion",
        headerName: "Última Actualización",
        flex: 1,
        align: "left",
        headerAlign: "left",
        minWidth: 200,
        renderCell: (params) => {
          const fechaActualizacion = new Date(params.value).toLocaleString("es-AR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          const isRecent = new Date() - new Date(params.value) < 300000

          return (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-start",
                gap: 1,
                alignItems: "center",
              }}
            >
              <CalendarTodayIcon
                fontSize="small"
                sx={{
                  color: isRecent ? "#4CAF50" : alpha("#5b5b5f", 0.7),
                  animation: isRecent ? "pulse 2s infinite" : "none",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isRecent ? "#4CAF50" : "inherit",
                  fontWeight: isRecent ? "bold" : "normal",
                }}
              >
                {fechaActualizacion}
              </Typography>
            </Box>
          )
        },
      },
      {
        field: "montoTotal",
        headerName: "Monto",
        type: "number",
        flex: 0.8,
        minWidth: 130,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
            <Chip
              icon={<AttachMoneyIcon fontSize="small" />}
              label={params.value?.toLocaleString("es-AR") || "0.00"}
              size="small"
              sx={{
                fontWeight: "bold",
                bgcolor: alpha("#4CAF50", 0.1),
                color: "#4CAF50",
                "& .MuiChip-icon": {
                  color: "#4CAF50",
                },
                display: "flex",
              }}
            />
          </Box>
        ),
      },
      {
        field: "descripcionEntidad",
        headerName: "Cliente",
        flex: 1.2,
        align: "left",
        headerAlign: "left",
        minWidth: 150,
        renderCell: (params) => (
          <Tooltip title={`${params.row.descripcionEntidad} ${params.row.apellido || ""}`}>
            <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start" }}>
              <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
              <Typography variant="body2" noWrap fontWeight={"500"}>
                {params.row.descripcionEntidad} {params.row.apellido || ""}
              </Typography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "estado",
        headerName: "Estado",
        flex: 0.8,
        minWidth: 120,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => {
          const estadoInfo = estadoConfig[params.value]
          const Icon = estadoInfo.icon
          return (
            <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
              <Chip
                icon={<Icon fontSize="small" />}
                label={estadoInfo.label}
                size="small"
                sx={{
                  fontWeight: "bold",
                  bgcolor: estadoInfo.bgColor,
                  color: estadoInfo.color,
                  "& .MuiChip-icon": {
                    color: estadoInfo.color,
                  },
                  display: "flex",
                }}
              />
            </Box>
          )
        },
      },
      {
        field: "medioPago",
        headerName: "Medio de Pago",
        flex: 1,
        minWidth: 200,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
            <Chip
              icon={<PointOfSaleIcon fontSize="small" />}
              label={params.value || "N/A"}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: "bold",
                color: params.value === "Sin definir" ? "#FF9800" : "#00AAE4",
                borderColor: alpha(params.value === "Sin definir" ? "#FF9800" : "#00AAE4", 0.5),
                "& .MuiChip-icon": { color: params.value === "Sin definir" ? "#FF9800" : "#00AAE4" },
                display: "flex",
              }}
            />
          </Box>
        ),
      },
      {
        field: "usuario",
        headerName: "Vendedor",
        flex: 0.8,
        minWidth: 150,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "ubicacion",
        headerName: "Ubicación",
        flex: 1,
        minWidth: 150,
        align: "left",
        headerAlign: "left",
        renderCell: (params) => (
          <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start" }}>
            <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2">{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "action",
        headerName: "Acciones",
        flex: 0.8,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <DataGridActionCell actions={getVentasEstadoRowActions(params.row)} />
          </Box>
        ),
      },
    ],
    [getVentasEstadoRowActions],
  )

  const totalVentas = useMemo(() => sortedData.reduce((acc, row) => acc + (row.montoTotal || 0), 0), [sortedData])

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          padding: "10px",
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "right",
          width: "100%",
          backgroundColor: "#f5f5f5",
        }}
      >
        Total Ventas: <span style={{ color: "#4CAF50" }}>${totalVentas.toLocaleString("es-AR")}</span>
      </div>
      <GridFooter />
    </GridFooterContainer>
  )

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Contenedor de filtros */}
        <Box
          sx={{
            px: "16px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              alignItems: "center",
              gap: "20px",
              width: "100%",
              minWidth: 0,
              overflowX: "auto",
              paddingTop: "5px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: "0 0 auto" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <IconButton onClick={decreaseDate} color="primary" size="small">
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <DatePicker
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Desde"
                  slotProps={{
                    textField: { size: "small", inputProps: { maxLength: 10 } },
                  }}
                  sx={{ width: 150 }}
                />
                <DatePicker
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  label="Hasta"
                  slotProps={{
                    textField: { size: "small", inputProps: { maxLength: 10 } },
                  }}
                  sx={{ width: 150 }}
                />
                <IconButton onClick={increaseDate} color="primary" size="small">
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </LocalizationProvider>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: "0 0 auto",
              }}
            >
              <TextField
                label="Mínimo"
                type="number"
                size="small"
                value={minVentas}
                onChange={handleMinChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{ width: "7rem" }}
              />
              <TextField
                label="Máximo"
                type="number"
                size="small"
                value={maxVentas}
                onChange={handleMaxChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{ width: "7rem" }}
              />
            </Box>

            <Box
              sx={{
                flex: "1 1 0%",
                minWidth: 0,
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                columnGap: 1,
                alignItems: "center",
              }}
            >
              <TextField
                label="Buscar por ID"
                size="small"
                value={idTransaccionSearch}
                onChange={(e) => setIdTransaccionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 0, width: "100%" }}
              />
              <Autocomplete
                size="small"
                disablePortal
                sx={{ minWidth: 0, width: "100%" }}
                options={ubicaciones.map((it) => ({
                  id: it.id,
                  label: it.descripcion,
                  descripcion: it.descripcion,
                }))}
                value={selectedUbicacion}
                onChange={(event, newValue) => setSelectedUbicacion(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Ubicación"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <WarehouseIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Autocomplete
                size="small"
                disablePortal
                sx={{ minWidth: 0, width: "100%" }}
                options={entidadesClientesTresLetras.map((it) => ({
                  label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                  ...it,
                }))}
                value={dniCliente}
                onChange={(event, newValue) => setDniCliente(newValue || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Autocomplete
                size="small"
                disablePortal
                sx={{ minWidth: 0, width: "100%" }}
                options={Object.entries(estadoConfig).map(([key, config]) => ({
                  value: key,
                  label: config.label,
                }))}
                value={estadoFilter ? { value: estadoFilter, label: estadoConfig[estadoFilter].label } : null}
                onChange={(event, newValue) => setEstadoFilter(newValue?.value || "")}
                renderInput={(params) => <TextField {...params} label="Estado" variant="outlined" />}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "0 0 auto" }}>
              <Tooltip title="Limpiar filtros">
                <span style={{ display: "inline-flex" }}>
                  <IconButton
                    onClick={resetFilters}
                    disabled={loading || !hayFiltrosParaLimpiar}
                    size="small"
                    aria-label="Limpiar filtros"
                    sx={{
                      width: 36,
                      height: 36,
                      padding: 0,
                      boxSizing: "border-box",
                      borderRadius: "8px",
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                      bgcolor: "transparent",
                      color: theme.palette.text.secondary,
                      "&:hover": { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refrescar">
                <span style={{ display: "inline-flex" }}>
                  <IconButton
                    onClick={refreshPedidos}
                    disabled={loading}
                    color="primary"
                    size="small"
                    aria-label="Refrescar"
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
        </Box>

        {/* Contenedor de la tabla */}
        <Paper
          onContextMenuCapture={handleEstadosContextMenu}
          sx={{
            flex: 1,
            m: "16px",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            height: "500px",
          }}
        >
          <DataGridRowContextMenu
            contextMenu={estadosContextMenu}
            onClose={handleEstadosContextMenuClose}
          />
          <DataGrid
            rows={sortedData}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            pagination
            getRowHeight={() => 35}
            headerHeight={35}
            sx={{
              width: "100%",
              height: "100%",
              border: 0,
              borderRadius: "8px",
              overflow: "hidden",
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
                borderRadius: "8px 8px 0 0",
              },
                "& .MuiDataGrid-container--top [role=row]": { backgroundColor: "#5b5b5f" },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
              "@keyframes pulse": {
                "0%": {
                  opacity: 1,
                },
                "50%": {
                  opacity: 0.5,
                },
                "100%": {
                  opacity: 1,
                },
              },
            }}
            components={{
              Pagination: () => null,
              Footer: CustomFooter,
            }}
          />
        </Paper>

        {/* Modal de detalles */}
        {openModal && selectedOrder && (
          <OrderDetailModal
            open={openModal}
            onClose={handleCloseModal}
            order={selectedOrder}
            onStatusChange={handleStatusChange}
            canChangeToState={canChangeToState}
          />
        )}

        {/* Notificaciones */}
        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            variant="filled"
            sx={{
              width: "100%",
              "& .MuiAlert-icon": {
                fontSize: "1.5rem",
              },
            }}
            icon={notification.severity === "success" ? <CheckIcon /> : <ErrorIcon />}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </div>
  )
}

// Componente Modal de detalles
function OrderDetailModal({ open, onClose, order, onStatusChange, canChangeToState }) {
  const handleStatusChange = (newStatus) => {
    onStatusChange(order.id, newStatus)
  }

  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: open ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
        }}
        onClick={onClose}
      >
        <Paper
          sx={{
            width: "90%",
            maxWidth: "800px",
            maxHeight: "90%",
            overflow: "auto",
            p: 3,
            borderRadius: 3,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h5" fontWeight="bold">
              Detalles del Pedido #{order.id}
            </Typography>
            <Button onClick={onClose} variant="outlined" size="small">
              Cerrar
            </Button>
          </Box>

          {/* Información del cliente */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: "#f8f9fa" }}>
            <Typography variant="h6" gutterBottom>
              Información del Cliente
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {order.descripcionEntidad} {order.apellido}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  DNI/CUIT
                </Typography>
                <Typography variant="body1">{order.dniCuitCuil}</Typography>
              </Box>
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography variant="body2" color="text.secondary">
                  Dirección
                </Typography>
                <Typography variant="body1">{order.direccion}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Estado del pedido */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: "#f8f9fa" }}>
            <Typography variant="h6" gutterBottom>
              Estado del Pedido
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="body2">Estado actual:</Typography>
              <Chip
                icon={React.createElement(estadoConfig[order.estado].icon, { fontSize: "small" })}
                label={estadoConfig[order.estado].label}
                sx={{
                  bgcolor: estadoConfig[order.estado].bgColor,
                  color: estadoConfig[order.estado].color,
                  fontWeight: "bold",
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Flujo: Pendiente → Recibido → Despachado → Entregado
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {Object.entries(estadoConfig).map(([estado, config]) => {
                const isCurrentState = order.estado === estado
                const canChange = canChangeToState(order.estado, estado)
                const isDisabled = !isCurrentState && !canChange

                return (
                  <Button
                    key={estado}
                    variant={isCurrentState ? "contained" : "outlined"}
                    size="small"
                    onClick={() => !isDisabled && handleStatusChange(estado)}
                    startIcon={React.createElement(config.icon, { fontSize: "small" })}
                    disabled={isDisabled}
                    sx={{
                      color: isDisabled ? "#ccc" : config.color,
                      borderColor: isDisabled ? "#ccc" : config.color,
                      "&:hover": {
                        bgcolor: isDisabled ? "transparent" : config.bgColor,
                      },
                      ...(isCurrentState && {
                        bgcolor: config.color,
                        color: "white",
                      }),
                    }}
                  >
                    {config.label}
                  </Button>
                )
              })}
            </Box>
          </Paper>

          {/* Información de venta */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: "#f8f9fa" }}>
            <Typography variant="h6" gutterBottom>
              Información de Venta
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Fecha
                </Typography>
                <Typography variant="body1">{order.createdAt}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Vendedor
                </Typography>
                <Typography variant="body1">{order.usuario}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Ubicación
                </Typography>
                <Typography variant="body1">{order.ubicacion}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Medio de Pago
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: order.medioPago === "Sin definir" ? "#FF9800" : "inherit",
                    fontWeight: order.medioPago === "Sin definir" ? "bold" : "normal",
                  }}
                >
                  {order.medioPago}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Monto Total
                </Typography>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  ${order.montoTotal.toLocaleString("es-AR")}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Items del pedido */}
          <Paper sx={{ p: 2, bgcolor: "#f8f9fa" }}>
            <Typography variant="h6" gutterBottom>
              Items del Pedido
            </Typography>
            <Box sx={{ overflow: "auto" }}>
              {order.items.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr 1fr 1fr 1fr",
                    gap: 2,
                    p: 1,
                    borderBottom: index < order.items.length - 1 ? "1px solid #ddd" : "none",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" fontFamily="monospace">
                    {item.codigo}
                  </Typography>
                  <Typography variant="body2">{item.descripcion}</Typography>
                  <Typography variant="body2" textAlign="center">
                    {item.cantidad}
                  </Typography>
                  <Typography variant="body2" textAlign="right">
                    ${item.precio.toLocaleString("es-AR")}
                  </Typography>
                  <Typography variant="body2" textAlign="right" fontWeight="bold">
                    ${(item.cantidad * item.precio).toLocaleString("es-AR")}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Paper>
      </div>
    </ThemeProvider>
  )
}
